import { AiJobProgressStatus, AiJobSceneDTO, LabEngineRequest } from 'models/photoStudio'
import { JobDTO, JobProgressDTO } from 'models/misc'
import { JobStatus, PhotoStudioLabEndpoint } from 'constants/API'

import type { AIVisualsResultResponse } from 'dataQueries/photoStudio.query'
import { isAxiosError } from 'axios'
import { useAPI } from 'utils/API'
import { useCallback, useMemo, useRef } from 'react'
import { useLabJobSocket } from './useLabJobSocket.hook'

/** Poll of a running job, kept up alongside the socket (best effort) — a frame that settles the job wakes it early */
export const LAB_JOB_POLL_MS = 2_500

/** Failed polls in a row tolerated before the run fails — a network blip must not cost a 90-second generation */
export const LAB_JOB_POLL_MAX_FAILURES = 3

export interface WaitForLabJobOptions {
  signal: AbortSignal
  timeoutMs: number
  /** Progress of the running job, from the socket and the poll alike — never older than a report already made */
  onProgress?: (progress: JobProgressDTO) => void
  /** A step the job just finished, to stage live — socket only: the poll never carries one */
  onScene?: (scene: AiJobSceneDTO) => void
}

/** Resolves after `ms`, or as soon as `wake` is called; rejects when the signal aborts */
const pause = (ms: number, signal: AbortSignal, onWakeReady: (wake: () => void) => void) => new Promise<void>((resolve, reject) => {
  const onAbort = () => {
    clearTimeout(timer)
    reject(signal.reason)
  }
  const wake = () => {
    clearTimeout(timer)
    signal.removeEventListener('abort', onAbort)
    resolve()
  }
  const timer = setTimeout(wake, ms)
  signal.addEventListener('abort', onAbort, { once: true })
  onWakeReady(wake)
})

/** A 4xx will not heal on the next poll (the flag is off server-side, the seat lacks the tool, the job is gone) */
const isPermanentFailure = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined
  return status !== undefined && status >= 400 && status < 500
}

/**
 * Photo Studio's Lab engine through the platform: start a tool, follow its job, read its result. The browser never
 * talks to the Lab — no key, no Lab URL — only to platform endpoints with the user's own session.
 */
export const useLabEngine = (assignmentId: string | undefined, isEnabled: boolean) => {
  // useAPI hands out a new object on each render; a ref keeps the engine stable for the context that shares it
  const api = useAPI<PhotoStudioLabEndpoint>()
  const apiRef = useRef(api)
  apiRef.current = api
  const { watchJob } = useLabJobSocket(assignmentId, isEnabled)

  /** Starts a tool; resolves with its job id */
  const startJob = useCallback(async ({ tool, body }: LabEngineRequest): Promise<string> => {
    if (!assignmentId) throw new Error('lab_engine_missing_assignment')
    const job = await apiRef.current.post<JobDTO>(PhotoStudioLabEndpoint.START_TOOL, { assignmentId, tool }, body, false)
    return job.id
  }, [assignmentId])

  /** Resolves once the job FINISHED; rejects when it FAILED, the deadline passed or the signal aborted */
  const waitForJob = useCallback(async (jobId: string, { signal, timeoutMs, onProgress, onScene }: WaitForLabJobOptions): Promise<void> => {
    let outcome: { status: Exclude<AiJobProgressStatus, 'RUNNING'>, error?: string } | undefined
    let lastPercent = -1
    let wake: (() => void) | undefined
    // Socket frames and polls overlap: a report behind one already made (a lower overall percent) is stale
    const report = (progress: JobProgressDTO) => {
      const percent = progress.percent ?? lastPercent
      if (percent < lastPercent) return
      lastPercent = percent
      onProgress?.(progress)
    }
    const stopWatching = watchJob(jobId, (frame) => {
      // A scene comes alone: it carries no progress to report
      if (frame.scene) {
        onScene?.(frame.scene)
        return
      }
      if (frame.status === 'RUNNING') {
        report(frame)
        return
      }
      outcome = { status: frame.status, error: frame.error ?? undefined }
      wake?.()
    })

    const deadline = Date.now() + timeoutMs
    let failedPolls = 0
    try {
      for (;;) {
        signal.throwIfAborted()
        if (!outcome) {
          try {
            const job = await apiRef.current.get<JobDTO>(PhotoStudioLabEndpoint.JOB, { id: jobId }, false)
            failedPolls = 0
            if (job.status === JobStatus.FINISHED) outcome = { status: 'FINISHED' }
            // The platform's job carries no reason (only the socket frame does): a failure seen here is lab_job_failed
            else if (job.status === JobStatus.FAILED) outcome = { status: 'FAILED' }
            else if (job.progress) report(job.progress)
          } catch (error) {
            failedPolls += 1
            if (isPermanentFailure(error) || failedPolls >= LAB_JOB_POLL_MAX_FAILURES) throw error
          }
          signal.throwIfAborted()
        }
        if (outcome?.status === 'FINISHED') return
        if (outcome?.status === 'FAILED') throw new Error(outcome.error || 'lab_job_failed')
        if (Date.now() > deadline) throw new Error('lab_job_timeout')
        await pause(LAB_JOB_POLL_MS, signal, (resolve) => {
          wake = resolve
        })
        wake = undefined
      }
    } finally {
      stopWatching()
    }
  }, [watchJob])

  /** Downloads a finished job's result — the image of a tool job, the segments JSON of an `objects` job */
  const fetchResult = useCallback(async (jobId: string, signal: AbortSignal): Promise<Response> => {
    if (!assignmentId) throw new Error('lab_engine_missing_assignment')
    const { signedURL } = await apiRef.current.get<AIVisualsResultResponse>(PhotoStudioLabEndpoint.RESULT, { assignmentId }, false, { params: { jobId } })
    const response = await fetch(signedURL, { signal })
    if (!response.ok) throw new Error(`lab_result_download_failed: HTTP ${response.status}`)
    return response
  }, [assignmentId])

  return useMemo(() => ({ startJob, waitForJob, fetchResult }), [startJob, waitForJob, fetchResult])
}

export type LabEngine = ReturnType<typeof useLabEngine>
